import React, { useRef, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Copy, Shuffle, RotateCcw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useDrawPresence } from "@/hooks/useDrawPresence";
import { useVoiceAnnouncer } from "@/components/VoiceAnnouncer";
import { usePerformance } from "@/contexts/PerformanceContext";

type Props = {
  min: number;
  max: number;
  count: number;
  onResult?: (numbers: number[]) => void;
};

const rangeArray = (min: number, max: number) => {
  const arr = [];
  for (let i = min; i <= max; i++) arr.push(i);
  return arr;
};

export const SlotSpinner = ({ min, max, count, onResult }: Props) => {
  const { sessionId, registerDraw } = useDrawPresence();
  const { lightModeEnabled } = usePerformance();
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState<number | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [usedNumbers, setUsedNumbers] = useState<Set<number>>(new Set());
  const [displayNumber, setDisplayNumber] = useState<number>(min);
  const [blurAmount, setBlurAmount] = useState(0);
  const [winningNumber, setWinningNumber] = useState<number | null>(null);
  const [rigged, setRigged] = useState(false);
  const [finalDraw, setFinalDraw] = useState(false);
  const [voiceEnabled] = useState(!lightModeEnabled); // Disable voice on light mode
  const [announcerStyle] = useState<"gameshow" | "casino" | "party" | "professional">("casino");
  const [showBalloons, setShowBalloons] = useState(false);
  const [hasAnnounced3, setHasAnnounced3] = useState(false);
  const [hasAnnounced2, setHasAnnounced2] = useState(false);
  const [hasAnnounced1, setHasAnnounced1] = useState(false);
  const [countdown, setCountdown] = useState(10);

  const { announceThree, announceTwo, announceWinner, isSpeaking } = useVoiceAnnouncer({ style: announcerStyle });

  const animationFrameRef = useRef<number>();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const spinAudioRef = useRef<HTMLAudioElement | null>(null);
  const numberPool = rangeArray(min, max);

  useEffect(() => {
    // Skip audio initialization in light mode
    if (lightModeEnabled) return;

    // Create audio element for winning sound
    audioRef.current = new Audio('https://assets.mixkit.co/active_storage/sfx/2000/2000-preview.mp3');
    audioRef.current.volume = 0.5;
    
    // Create audio element for slot machine spinning sound
    spinAudioRef.current = new Audio('https://assets.mixkit.co/active_storage/sfx/1934/1934-preview.mp3');
    spinAudioRef.current.volume = 0.4;
    spinAudioRef.current.loop = true;
    
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (spinAudioRef.current) {
        spinAudioRef.current.pause();
        spinAudioRef.current = null;
      }
    };
  }, [lightModeEnabled]);

  const fetchBackendNumber = async (): Promise<{ number: number | null; isFinalDraw: boolean; rigged: boolean }> => {
    const TIMEOUT = 8000; // 8 second timeout
    
    try {
      console.log('🎲 Fetching backend number with params:', {
        min,
        max,
        count: 1,
        usedNumbers: Array.from(usedNumbers).length,
        sessionId
      });

      // Create timeout promise
      const timeoutPromise = new Promise<never>((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), TIMEOUT)
      );

      // Race between API call and timeout
      const apiPromise = supabase.functions.invoke('generate-numbers', {
        body: {
          min,
          max,
          count: 1,
          usedNumbers: Array.from(usedNumbers),
          sessionId
        }
      });

      const { data, error } = await Promise.race([apiPromise, timeoutPromise]);

      if (error) {
        console.error('❌ Backend generation error:', error);
        toast.error('Failed to generate number');
        return { number: null, isFinalDraw: false, rigged: false };
      }

      // Validate response
      if (!data || !data.numbers || data.numbers.length === 0) {
        console.error('❌ Invalid response: no numbers returned', data);
        toast.error('No number returned from backend');
        return { number: null, isFinalDraw: false, rigged: false };
      }

      const receivedNumber = data.numbers[0];
      
      // Validate number is valid
      if (receivedNumber === null || receivedNumber === undefined || typeof receivedNumber !== 'number') {
        console.error('❌ Invalid number received:', receivedNumber);
        toast.error('Invalid number from backend');
        return { number: null, isFinalDraw: false, rigged: false };
      }

      const rigged = data?.source === 'queue' || data?.forcedApplied === true;
      console.log('✅ Backend number fetched:', receivedNumber, '| Rigged:', rigged, '| Final:', data.isFinalDraw);
      
      return { 
        number: receivedNumber, 
        isFinalDraw: data.isFinalDraw || false,
        rigged
      };
      
    } catch (err) {
      console.error('❌ Unexpected error:', err);
      
      if (err instanceof Error && err.message === 'Request timeout') {
        toast.error('Connection timeout. Please check your internet and try again.');
      } else {
        toast.error('Failed to generate number');
      }
      
      return { number: null, isFinalDraw: false, rigged: false };
    }
  };

  const spin = async () => {
    // Prevent double-clicking
    if (spinning) {
      console.log('⚠️ Spin prevented - already spinning');
      return;
    }
    
    console.log('🎯 Spin started');
    setSpinning(true);
    setResult(null);
    setBlurAmount(0);
    setWinningNumber(null);
    setRigged(false);

    try {
      // Register presence
      await registerDraw(min, max, count);

      // Fetch backend number immediately to avoid delays
      const backendResult = fetchBackendNumber();

      // In light mode, skip animation and show result immediately
      if (lightModeEnabled) {
        const result = await backendResult;
        if (!result.number) {
          console.error('❌ Failed to get backend number in light mode');
          setSpinning(false);
          toast.error('Failed to generate number');
          return;
        }
        
        console.log('✅ Light mode result:', result.number);
        const newUsed = new Set(usedNumbers);
        newUsed.add(result.number);
        setUsedNumbers(newUsed);
        
        setSpinning(false);
        setResult(result.number);
        setRigged(result.rigged);
        setFinalDraw(result.isFinalDraw);
        setShowModal(true);
        setDisplayNumber(result.number);
        onResult?.([result.number]);
        
        const remaining = numberPool.length - newUsed.size;
        const isWinner = result.rigged || result.isFinalDraw || (winningNumber !== null && result.number === winningNumber);
        
        if (isWinner) {
          toast.success(`🎉 WINNER! You got ${result.number}!`);
        } else {
          toast.error(`❌ Not a winner. ${remaining} number${remaining !== 1 ? 's' : ''} remaining.`);
        }
        return;
      }

    // Start spinning sound
    if (spinAudioRef.current) {
      spinAudioRef.current.currentTime = 0;
      spinAudioRef.current.play().catch(e => console.error('Spin audio play failed:', e));
    }

    const spinDuration = 3000;
    const startTime = performance.now();
    let finalNumber: number | null = null;
    let isFinalDraw = false;
    let isRigged = false;
    let frameCount = 0;
    
    const animate = async (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / spinDuration, 1);
      frameCount++;
      
      // Get backend result when it's ready (around 60% progress)
      if (progress >= 0.6 && !finalNumber) {
        const result = await backendResult;
        finalNumber = result.number;
        isFinalDraw = result.isFinalDraw;
        isRigged = result.rigged;
        
        if (!finalNumber) {
          console.error('❌ No final number received from backend');
          setSpinning(false);
          if (spinAudioRef.current) {
            spinAudioRef.current.pause();
          }
          toast.error('Failed to complete spin. Please try again.');
          return;
        }
        
        console.log('✅ Final number set:', finalNumber);
        
        if (isRigged) {
          setWinningNumber(finalNumber);
        }
      }
      
      // Optimized: Update every 2nd frame instead of every 3rd for smoother animation
      const shouldUpdate = frameCount % 2 === 0;
      
      if (shouldUpdate) {
        const speed = Math.max(20, 300 * (1 - progress));
        const currentBlur = Math.min(8, speed / 40);
        setBlurAmount(currentBlur);
        
        // Batch state updates together to reduce re-renders
        if (progress < 0.95) {
          const pool = finalNumber ? numberPool.filter(n => n !== finalNumber) : numberPool;
          const randomNum = pool[Math.floor(Math.random() * pool.length)] ?? (finalNumber ?? numberPool[0]);
          setDisplayNumber(randomNum);
        } else if (progress < 1 && finalNumber) {
          // Final reveal phase - show nearby numbers for suspense
          let nearbyNumbers = [finalNumber - 2, finalNumber - 1, finalNumber + 1, finalNumber + 2]
            .filter(n => n >= min && n <= max);
          if (nearbyNumbers.length === 0) {
            nearbyNumbers = numberPool.filter(n => n !== finalNumber);
          }
          const randomNearby = nearbyNumbers[Math.floor(Math.random() * nearbyNumbers.length)] ?? finalNumber;
          setDisplayNumber(randomNearby);
        }
      }
      
      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        if (finalNumber) {
          console.log('✅ Spin animation complete');
          setBlurAmount(0);
          setDisplayNumber(finalNumber);
          setTimeout(() => {
            finishSpin(finalNumber!, isFinalDraw, isRigged);
          }, 200);
        } else {
          console.error('❌ Spin completed but no final number');
          setSpinning(false);
          if (spinAudioRef.current) {
            spinAudioRef.current.pause();
          }
          toast.error('Spin failed. Please try again.');
        }
      }
    };
    
    animationFrameRef.current = requestAnimationFrame(animate);
    
    } catch (error) {
      console.error('❌ Spin error:', error);
      setSpinning(false);
      setBlurAmount(0);
      if (spinAudioRef.current) {
        spinAudioRef.current.pause();
        spinAudioRef.current.currentTime = 0;
      }
      toast.error('Something went wrong. Please try again.');
    }
  };

  const finishSpin = (finalNumber: number, isFinalDraw: boolean, isRigged: boolean) => {
    if (spinAudioRef.current) {
      spinAudioRef.current.pause();
      spinAudioRef.current.currentTime = 0;
    }

    const newUsed = new Set(usedNumbers);
    newUsed.add(finalNumber);
    setUsedNumbers(newUsed);
    
    const remaining = numberPool.length - newUsed.size;
    const isWinner = isRigged || isFinalDraw || (winningNumber !== null && finalNumber === winningNumber);
    
    setSpinning(false);
    setResult(finalNumber);
    setRigged(isRigged);
    setFinalDraw(isFinalDraw);
    setShowModal(true);
    onResult?.([finalNumber]);
    
    // Voice announcements (non-blocking - fire and forget)
    if (voiceEnabled && !isSpeaking && !lightModeEnabled) {
      if (remaining === 3 && !hasAnnounced3) {
        setHasAnnounced3(true);
        announceThree(finalNumber); // Non-blocking
      } else if (remaining === 2 && !hasAnnounced2) {
        setHasAnnounced2(true);
        announceTwo(finalNumber); // Non-blocking
      } else if (remaining === 1 && !hasAnnounced1) {
        setHasAnnounced1(true);
        announceTwo(finalNumber); // Non-blocking
      } else if (isWinner) {
        // Skip balloons in light mode
        if (!lightModeEnabled) {
          setShowBalloons(true);
          setTimeout(() => setShowBalloons(false), 5000);
        }
        announceWinner(finalNumber); // Non-blocking
      }
    }
    
    if (isWinner) {
      // Skip audio in light mode
      if (audioRef.current && !lightModeEnabled) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(e => console.error('Audio play failed:', e));
      }
      toast.success(`🎉 WINNER! You got ${finalNumber}!`);
    } else {
      toast.error(`❌ Not a winner. ${remaining} number${remaining !== 1 ? 's' : ''} remaining.`);
    }
  };

  const handleReset = () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    // Stop spinning sound if playing
    if (spinAudioRef.current) {
      spinAudioRef.current.pause();
      spinAudioRef.current.currentTime = 0;
    }
    
    // Clear session storage and cache
    localStorage.removeItem('draw_session_id');
    
    // Clear component state
    setUsedNumbers(new Set());
    setResult(null);
    setShowModal(false);
    setDisplayNumber(min);
    setBlurAmount(0);
    setSpinning(false);
    setFinalDraw(false);
    setShowBalloons(false);
    setHasAnnounced3(false);
    setHasAnnounced2(false);
    setHasAnnounced1(false);
    
    console.log('✅ Cache cleared - session reset');
    toast.success("Cache cleared! Session reset.");
  };

  const handleCopy = () => {
    if (result) {
      navigator.clipboard.writeText(result.toString());
      toast.success("Number copied to clipboard!");
    }
  };

  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);

  // Auto-close modal after 10 seconds with countdown
  useEffect(() => {
    if (showModal) {
      setCountdown(10);
      
      const countdownInterval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      const timer = setTimeout(() => {
        setShowModal(false);
      }, 10000);
      
      return () => {
        clearTimeout(timer);
        clearInterval(countdownInterval);
      };
    }
  }, [showModal]);

  return (
    <>
      {/* Sparkle celebration animation */}
      {showBalloons && (
        <div className="fixed inset-0 z-50 pointer-events-none">
          {/* Gold flash */}
          <div className="absolute inset-0 bg-gradient-radial from-yellow-400/30 via-transparent to-transparent animate-[fade-in_0.3s_ease-out,fade-out_0.5s_ease-out_0.3s]" />
          
          {/* Sparkles */}
          <div className="absolute inset-0">
            {[...Array(40)].map((_, i) => {
              const angle = (i / 40) * Math.PI * 2;
              const distance = 20 + Math.random() * 30;
              const size = 2 + Math.random() * 4;
              return (
                <div
                  key={i}
                  className="absolute rounded-full animate-[scale-in_0.4s_ease-out,fade-out_0.6s_ease-out_0.4s]"
                  style={{
                    left: `${50 + Math.cos(angle) * distance}%`,
                    top: `${50 + Math.sin(angle) * distance}%`,
                    width: `${size}px`,
                    height: `${size}px`,
                    background: i % 3 === 0 ? 'linear-gradient(135deg, #fbbf24, #f59e0b)' : i % 3 === 1 ? 'linear-gradient(135deg, #8b5cf6, #7c3aed)' : 'linear-gradient(135deg, #ffffff, #e5e7eb)',
                    boxShadow: '0 0 8px currentColor',
                    animationDelay: `${i * 0.015}s`,
                  }}
                />
              );
            })}
          </div>
          
          {/* Particle burst */}
          <div className="absolute inset-0 flex items-center justify-center">
            {[...Array(30)].map((_, i) => {
              const angle = (i / 30) * Math.PI * 2;
              const speed = 15 + Math.random() * 15;
              return (
                <div
                  key={`particle-${i}`}
                  className="absolute w-1.5 h-1.5 rounded-full bg-gradient-to-br from-primary to-accent animate-[fade-out_1s_ease-out]"
                  style={{
                    transform: `translate(${Math.cos(angle) * speed}vw, ${Math.sin(angle) * speed}vh)`,
                    animationDelay: `${i * 0.02}s`,
                  }}
                />
              );
            })}
          </div>
        </div>
      )}

      <div className="w-full max-w-5xl mx-auto relative">
        {/* Decorative glow effects - Skip in light mode */}
        {!lightModeEnabled && (
          <>
            <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-96 h-96 bg-primary/20 rounded-full blur-3xl opacity-50 animate-pulse" />
            <div className="absolute -bottom-20 left-1/2 -translate-x-1/2 w-96 h-96 bg-accent/20 rounded-full blur-3xl opacity-50 animate-pulse" style={{ animationDelay: '1s' }} />
          </>
        )}
        
        <Card className={`relative ${!lightModeEnabled ? 'backdrop-blur-sm bg-gradient-to-br from-slate-900/95 via-slate-800/95 to-slate-900/95' : 'bg-card'} border-2 border-primary/30 shadow-2xl overflow-hidden`}>
          {/* Animated gradient overlay - Skip in light mode */}
          {!lightModeEnabled && (
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5 animate-pulse opacity-50" />
          )}
          
          <div className="relative p-12">
            <div className="text-center mb-8">
              <div className="flex items-center justify-center gap-3 mb-3">
                <Sparkles className={`w-8 h-8 text-primary ${!lightModeEnabled ? 'animate-pulse' : ''}`} />
                <h2 className="text-4xl font-black bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
                  Lucky Draw
                </h2>
                <Sparkles className={`w-8 h-8 text-accent ${!lightModeEnabled ? 'animate-pulse' : ''}`} style={{ animationDelay: '0.5s' }} />
              </div>
              <p className="text-muted-foreground text-lg">Press spin to reveal your number</p>
            </div>

            {/* Single large slot display */}
            <div className="flex items-center justify-center mb-10">
              <div className="relative">
                {/* Outer glow ring - Skip in light mode */}
                {!lightModeEnabled && (
                  <div className={`absolute -inset-4 bg-gradient-to-r from-primary via-accent to-primary rounded-2xl blur-xl opacity-75 ${spinning ? 'animate-pulse' : ''}`} />
                )}
                
                {/* Main slot machine display */}
                <div className="relative w-64 h-80 bg-gradient-to-b from-black via-slate-950 to-black rounded-2xl overflow-hidden border-4 border-primary/50 shadow-2xl">
                  {/* Inner shadow effect - Skip in light mode */}
                  {!lightModeEnabled && (
                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/5 to-transparent" />
                  )}
                  
                  {/* Number display */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div 
                      className={`text-8xl font-black bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent ${!lightModeEnabled && spinning ? 'transition-all duration-100' : 'transition-all duration-300 ease-out'}`}
                      style={!lightModeEnabled ? { 
                        filter: `blur(${blurAmount}px) drop-shadow(0 4px 12px rgba(139, 92, 246, 0.5))`,
                        textShadow: !spinning && result ? '0 0 40px rgba(251, 191, 36, 0.8), 0 0 20px rgba(251, 191, 36, 0.6)' : 'none',
                        transform: 'translateZ(0)', // GPU acceleration
                        willChange: spinning ? 'filter' : 'auto', // Optimize for filter changes during spin
                        backfaceVisibility: 'hidden', // Additional GPU optimization
                      } : {
                        transform: spinning ? 'scale(1.1) translateZ(0)' : result ? 'scale(1) translateZ(0)' : 'scale(0.85) translateZ(0)',
                        willChange: spinning ? 'transform' : 'auto',
                      }}
                    >
                      {displayNumber}
                    </div>
                  </div>

                  {/* Center highlight window */}
                  <div className="absolute inset-0 pointer-events-none">
                    <div className={`absolute top-[calc(50%-2.5rem)] left-0 right-0 h-20 border-y-4 border-primary/40 ${!lightModeEnabled ? 'bg-gradient-to-r from-transparent via-primary/10 to-transparent' : ''} transition-all duration-300 ${spinning ? 'border-primary/60 bg-primary/20' : ''}`} />
                    {/* Side shadows for depth - Skip in light mode */}
                    {!lightModeEnabled && (
                      <>
                        <div className="absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-black/80 to-transparent" />
                        <div className="absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-black/80 to-transparent" />
                      </>
                    )}
                  </div>

                  {/* Shine effect when stopped - Skip in light mode */}
                  {!lightModeEnabled && !spinning && result && (
                    <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent opacity-0 animate-[fade-in_0.5s_ease-out_forwards]" />
                  )}

                  {/* Speed lines during spin - Skip in light mode */}
                  {!lightModeEnabled && spinning && (
                    <>
                      <div className="absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-primary/30 to-transparent animate-pulse" />
                      <div className="absolute top-1/3 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-accent/30 to-transparent animate-pulse" style={{ animationDelay: '0.2s' }} />
                      <div className="absolute top-2/3 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-primary/30 to-transparent animate-pulse" style={{ animationDelay: '0.4s' }} />
                    </>
                  )}
                </div>

                {/* Corner decorations */}
                <div className="absolute -top-2 -left-2 w-6 h-6 border-t-4 border-l-4 border-primary rounded-tl-lg" />
                <div className="absolute -top-2 -right-2 w-6 h-6 border-t-4 border-r-4 border-accent rounded-tr-lg" />
                <div className="absolute -bottom-2 -left-2 w-6 h-6 border-b-4 border-l-4 border-accent rounded-bl-lg" />
                <div className="absolute -bottom-2 -right-2 w-6 h-6 border-b-4 border-r-4 border-primary rounded-br-lg" />
              </div>
            </div>

            {/* Control buttons */}
            <div className="flex items-center justify-center gap-6">
              <Button
                onClick={spin}
                disabled={spinning}
                size="lg"
                className="relative group bg-gradient-to-r from-primary via-accent to-primary hover:opacity-90 text-primary-foreground font-bold shadow-2xl transition-all duration-300 min-w-[200px] text-lg h-14 overflow-hidden disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ pointerEvents: spinning ? 'none' : 'auto' }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000" />
                <Shuffle className={`mr-2 h-6 w-6 ${spinning ? 'animate-spin' : ''}`} />
                {spinning ? "SPINNING…" : "SPIN NOW"}
              </Button>

              <Button
                onClick={handleReset}
                variant="outline"
                size="lg"
                disabled={spinning}
                className="border-2 border-primary/50 hover:bg-primary/20 hover:border-primary transition-all duration-300 h-14 text-lg font-semibold disabled:opacity-50"
              >
                <RotateCcw className="mr-2 h-5 w-5" />
                Reset
              </Button>
            </div>

            {/* Stats display */}
            <div className="mt-8 text-center">
              <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 border border-primary/30 rounded-full">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${spinning ? 'bg-accent animate-ping' : 'bg-primary animate-pulse'}`} />
                  <span className="text-sm font-semibold text-foreground">
                    {usedNumbers.size} / {numberPool.length} Numbers Used
                  </span>
                </div>
                {usedNumbers.size > 0 && (
                  <>
                    <div className="w-px h-4 bg-border" />
                    <span className="text-sm text-muted-foreground">
                      {numberPool.length - usedNumbers.size} Remaining
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Result Modal */}
      {showModal && result && (
        <div 
          role="dialog" 
          aria-modal="true" 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-in fade-in duration-300"
        >
          <div 
            className="absolute inset-0 bg-black/80 backdrop-blur-md" 
            onClick={() => setShowModal(false)} 
          />
          
          {(() => {
            const remaining = numberPool.length - usedNumbers.size;
            const isWinner = rigged || finalDraw || (winningNumber !== null && result === winningNumber);
            
            return (
              <Card className={`relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-8 w-full max-w-lg z-10 border-4 ${isWinner ? 'border-primary/50' : 'border-destructive/50'} shadow-2xl animate-in scale-in duration-500 overflow-hidden`}>
                {/* Animated background effect */}
                <div className={`absolute inset-0 bg-gradient-to-br ${isWinner ? 'from-primary/10 via-accent/10 to-primary/10' : 'from-destructive/10 via-destructive/5 to-destructive/10'} animate-pulse`} />
                
                {/* Glow effects */}
                <div className={`absolute -top-20 left-1/2 -translate-x-1/2 w-64 h-64 ${isWinner ? 'bg-primary/30' : 'bg-destructive/30'} rounded-full blur-3xl`} />
                <div className={`absolute -bottom-20 left-1/2 -translate-x-1/2 w-64 h-64 ${isWinner ? 'bg-accent/30' : 'bg-destructive/30'} rounded-full blur-3xl`} />
                
                <div className="relative">
                  {/* Title */}
                  <div className="flex items-center justify-center mb-4">
                    {isWinner ? (
                      <h2 className="text-3xl font-black bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
                        WINNER!
                      </h2>
                    ) : (
                      <h2 className="text-3xl font-black bg-gradient-to-r from-destructive to-destructive/60 bg-clip-text text-transparent">
                        Not a Winner
                      </h2>
                    )}
                  </div>

                  {/* Result number display */}
                  <div className={`mb-6 p-6 rounded-xl bg-gradient-to-br ${isWinner ? 'from-primary/20 to-accent/20 border-2 border-primary/30' : 'from-destructive/20 to-destructive/10 border-2 border-destructive/30'}`}>
                    <p className="text-sm text-muted-foreground text-center mb-2">Your Number</p>
                    <p className={`text-7xl font-black text-center bg-gradient-to-r ${isWinner ? 'from-primary via-accent to-primary' : 'from-destructive to-destructive/60'} bg-clip-text text-transparent`}>
                      {result}
                    </p>
                  </div>

                  {/* Message */}
                  <div className="text-center mb-6">
                    {isWinner ? (
                      <>
                        <p className="text-lg font-semibold text-foreground mb-2">
                          Congratulations! 🎊
                        </p>
                        <p className="text-sm text-muted-foreground">
                          You got the winning number!
                        </p>
                        {rigged && (
                          <p className="text-xs text-primary mt-2">
                            ⭐ Special Draw
                          </p>
                        )}
                      </>
                    ) : (
                      <>
                        <p className="text-lg font-semibold text-foreground mb-2">
                          Better luck next time!
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {remaining} number{remaining !== 1 ? 's' : ''} remaining
                        </p>
                      </>
                    )}
                  </div>

                  {/* Countdown timer */}
                  <div className="text-center mb-4">
                    <p className="text-xs text-muted-foreground">
                      Auto-closing in <span className="font-semibold text-foreground">{countdown}</span> seconds
                    </p>
                  </div>

                  {/* Action buttons */}
                  <div className="flex">
                    <Button
                      onClick={() => setShowModal(false)}
                      className="bg-gradient-to-r from-primary via-accent to-primary hover:opacity-90"
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })()}
        </div>
      )}
    </>
  );
};
